import React, { useEffect, useState } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import styled, { css, keyframes } from 'styled-components'
import { CircularProgress } from '@mui/material'
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined'
import { toast } from 'react-toastify'
import BpLogo from 'assets/images/bp-logo-orange.png'
import { getCurrentUser } from 'features/auth/ducks/selectors'
import { getUnitQrLandingInfo, resolveUnitForCurrentUser } from 'services/qrLandingService'
import { requestAccessToUnit } from 'services/unitsService'
import { PATHS } from 'utils/paths'
import LocalStorageService from 'services/LocalStorageService'
import { KEYS } from 'utils/localStorage'
import SearchableSelect from 'components/SearchableSelect'

// QR-landing intent: a tiny stash so that when an anonymous scanner
// clicks "Create account" we know on their return to remind them what
// they were trying to do — i.e. auto-submit a request-access without
// making them click again. localStorage (not session) because the
// email-verification step often returns the user in a different tab.
const INTENT_KEY = 'bp:qr-intent'
// Long enough for a careful brand-new user to finish: email arrival
// (~2-3 min), email click + verify, login, 2FA setup (~2 min for QR scan
// + first code). 60 min has plenty of slack without being so long that
// stale intents survive across sessions.
const INTENT_TTL_MS = 60 * 60 * 1000

const stashIntent = (unitId) => {
  try {
    LocalStorageService.set(INTENT_KEY, JSON.stringify({
      action: 'request-unit-access',
      unitId,
      relationType: 'occupant',
      ts: Date.now(),
    }))
  } catch { /* localStorage unavailable — silently drop, the request flow still works manually */ }
}

const consumeIntent = (unitId) => {
  try {
    const raw = LocalStorageService.get(INTENT_KEY)
    if (!raw) return null
    LocalStorageService.remove(INTENT_KEY)
    const parsed = JSON.parse(raw)
    if (parsed?.unitId !== unitId) return null
    if (parsed?.action !== 'request-unit-access') return null
    if (typeof parsed?.ts !== 'number' || Date.now() - parsed.ts > INTENT_TTL_MS) return null
    return parsed
  } catch { return null }
}

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`

const Page = styled.div`
  min-height: 100vh;
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 24px 16px;
  background: ${({ theme }) => theme.colors.lighterGrey};

  @media (min-width: 600px) {
    align-items: center;
    padding: 40px 24px;
  }
`

const Card = styled.div`
  width: 100%;
  max-width: 460px;
  background: ${({ theme }) => theme.colors.white};
  border-radius: 12px;
  box-shadow:
    0 10px 40px rgba(0, 0, 0, 0.08),
    0 0 0 1px rgba(0, 0, 0, 0.04);
  padding: 32px 28px;
  display: flex;
  flex-direction: column;
  gap: 18px;
  animation: ${fadeIn} 0.25s ease;

  @media (max-width: 576px) {
    padding: 24px 20px;
    border-radius: 14px;
  }
`

const Logo = styled.img`
  height: 34px;
  width: auto;
  align-self: center;
  margin-bottom: 4px;
`

const HeroIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.linen};
  color: ${({ theme }) => theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 4px;
`

const Title = styled.h1`
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti};
  text-align: center;
  line-height: 1.3;
`

const Subtitle = styled.p`
  margin: 0;
  text-align: center;
  font-size: 14px;
  color: #475569;
  line-height: 1.55;
`

const InfoBlock = styled.div`
  background: #f8fafc;
  border-radius: 10px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  border: 1px solid #eef2f7;
`

const InfoLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const InfoValue = styled.div`
  font-size: 15px;
  color: ${({ theme }) => theme.colors.haiti};
  font-weight: 600;
  line-height: 1.4;
`

const InfoSub = styled.div`
  font-size: 13px;
  color: #64748b;
  margin-top: 2px;
`

const SharedButtonStyle = css`
  width: 100%;
  height: 52px;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  border: none;
  transition: all 0.2s ease;

  &:disabled { opacity: 0.6; cursor: not-allowed; }
`

const PrimaryButton = styled.button`
  ${SharedButtonStyle}
  background: #110c2f;
  color: ${({ theme }) => theme.colors.white};

  &:hover:not(:disabled) {
    background: #36becd;
    transform: translateY(-1px);
  }
`

const SecondaryButton = styled.button`
  ${SharedButtonStyle}
  background: transparent;
  color: #110c2f;
  border: 2px solid #110c2f;

  &:hover:not(:disabled) {
    background: #f3f4f6;
  }
`

const ErrorState = styled.div`
  background: ${({ theme }) => theme.colors.linen};
  color: ${({ theme }) => theme.colors.error};
  border-radius: 10px;
  padding: 14px 16px;
  font-size: 14px;
  text-align: center;
  line-height: 1.5;
`

const Centered = styled.div`
  display: flex;
  justify-content: center;
  padding: 24px 0;
`

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const FieldLabel = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
`

const RELATION_OPTIONS = [
  { value: 'occupant', label: "I live here (occupant)" },
  { value: 'owner', label: 'I own this unit' },
  { value: 'agent', label: 'I am a managing agent' },
  { value: 'responsible_person', label: "I'm the responsible person" },
]

const UnitQrLanding = () => {
  const { unitId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const currentUser = useSelector(getCurrentUser)
  const isAuthed = !!currentUser?.id

  const [info, setInfo] = useState(null)        // public info (for anonymous visitors)
  const [resolved, setResolved] = useState(null) // auth'd resolve payload — used for the "request access" prompt below
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [loadAttempt, setLoadAttempt] = useState(0)
  const [relationType, setRelationType] = useState('occupant')
  const [submitting, setSubmitting] = useState(false)

  // Signed-in users hit the auth'd resolve endpoint and redirect
  // immediately — no public landing card flash. If they were the ones who
  // just signed up (intent stash matches), we also auto-submit an access
  // request before forwarding so the unit-detail page shows their request
  // already pending.
  useEffect(() => {
    if (!isAuthed) return
    let cancelled = false
    setError(null)
    setLoading(true)
    ;(async () => {
      try {
        const res = await resolveUnitForCurrentUser(unitId)
        if (cancelled) return
        const data = res?.data?.data
        if (!data) {
          setError(`We couldn't find this unit (id: ${unitId}).`)
          setLoading(false)
          return
        }
        if (data.unknown) {
          setError(`We couldn't find this unit (id: ${unitId}). The QR sticker may be out of date.`)
          setLoading(false)
          return
        }
        if (data.removed) {
          // Render a "removed" state — no redirect target, nothing to do.
          // Carry the building info forward so the card stays informative.
          setInfo({
            removed: true,
            unitName: data.unitName,
            buildingName: data.buildingName,
            buildingAddress: data.buildingAddress,
          })
          setLoading(false)
          return
        }

        const intent = consumeIntent(unitId)

        // Case A: user has access — straight through to the unit detail.
        if (data.hasAccess) {
          if (intent) toast.info('You already have access to this unit.')
          if (!cancelled) {
            navigate(`/bp-list/${data.buildingPassportId}/units/${data.unitId}`, { replace: true })
          }
          return
        }

        // Case B: user has no access AND just signed up via the QR → fire
        // the access request silently and forward to the unit page so the
        // pending request shows up there. 422 (already requested/assigned)
        // is a soft outcome we surface as an info toast.
        if (intent) {
          try {
            await requestAccessToUnit({
              bpId: data.buildingPassportId,
              unitId,
              relationType: intent.relationType || 'occupant',
            })
            toast.success('Access request sent. The building manager will review it.')
          } catch (err) {
            const status = err?.response?.status
            if (status === 422) {
              toast.info("You already have a pending request for this unit.")
            } else {
              toast.error("We couldn't send your access request automatically. You can request it from the unit page.")
            }
          }
          if (!cancelled) {
            navigate(`/bp-list/${data.buildingPassportId}/units/${data.unitId}`, { replace: true })
          }
          return
        }

        // Case C: authed but no access, no fresh intent (e.g. the user
        // was already signed in when they scanned). Show a one-click
        // "request access" card rather than dumping them on the unit
        // detail where the API would 403. We stop here and render the
        // resolved state — submit happens via handleRequestAccess below.
        setResolved(data)
        setLoading(false)
        return
      } catch (err) {
        if (cancelled) return
        const status = err?.response?.status
        if (status === 404) {
          setError(`We couldn't find this unit (id: ${unitId}).`)
        } else if (!status) {
          setError("We couldn't reach the server. Check your internet connection and try again.")
        } else {
          setError(`Something went wrong loading this unit (error ${status}).`)
        }
        setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [isAuthed, unitId, navigate, loadAttempt])

  // Anonymous visitors: fetch the lightweight public info so the card
  // can show "Flat 4 at 123 Main St" before they sign in.
  useEffect(() => {
    if (isAuthed) return
    let cancelled = false
    setLoading(true)
    setError(null)
    getUnitQrLandingInfo(unitId)
      .then(res => {
        if (cancelled) return
        setInfo(res?.data?.data || null)
      })
      .catch(err => {
        if (cancelled) return
        const status = err?.response?.status
        const body = err?.response?.data?.data
        if (status === 404) {
          if (body?.unknown) {
            setError(`We couldn't find this unit. The QR sticker may be out of date, or the link may be wrong. (id: ${unitId})`)
          } else {
            setError(`We couldn't find this unit (id: ${unitId}). The QR sticker may be out of date.`)
          }
        } else if (status === 429) {
          setError('Too many attempts. Please wait a moment and try again.')
        } else if (!status) {
          setError("We couldn't reach the server. Check your internet connection and try again.")
        } else {
          setError(`Something went wrong loading this unit (error ${status}).`)
        }
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [isAuthed, unitId, loadAttempt])

  // Sign-up: we DO stash the intent. A brand-new user has no prior context
  // about their unit relationship; once they confirm their email and log
  // in, the QR scan is enough signal to fire the request silently with
  // the default ('occupant'). The manager will adjust on approval if it's
  // wrong.
  const handleSignupWithIntent = () => {
    stashIntent(unitId)
    LocalStorageService.set(KEYS.redirectPath, location.pathname)
    navigate(PATHS.signup)
  }

  // Sign-in: an existing user already has a sense of their relationship
  // and may be a manager / owner rather than an occupant — silently
  // firing as 'occupant' would be presumptuous. So we DON'T stash. On
  // return, the resolve effect will fall through to Case C (the manual
  // request-access card with a relationship chooser), giving them an
  // explicit confirm step.
  const handleLogin = () => {
    LocalStorageService.set(KEYS.redirectPath, location.pathname)
    navigate(PATHS.login)
  }

  // One-click request-access for the "authed but no access, no intent"
  // case. We fire the request, then forward to the unit detail where the
  // pending state will be visible. 422 (already requested/assigned) is
  // surfaced quietly — it's a no-op the user shouldn't be punished for.
  const handleRequestAccess = async () => {
    if (!resolved) return
    setSubmitting(true)
    try {
      await requestAccessToUnit({
        bpId: resolved.buildingPassportId,
        unitId,
        relationType,
      })
      toast.success('Access request sent. The building manager will review it.')
      navigate(`/bp-list/${resolved.buildingPassportId}/units/${resolved.unitId}`, { replace: true })
    } catch (err) {
      const status = err?.response?.status
      if (status === 422) {
        toast.info("You already have a pending request for this unit.")
        navigate(`/bp-list/${resolved.buildingPassportId}/units/${resolved.unitId}`, { replace: true })
      } else {
        toast.error("We couldn't send your access request. Please try again.")
      }
    } finally {
      setSubmitting(false)
    }
  }

  // Quiet "Opening unit…" state for signed-in scanners who'll be
  // forwarded straight to the unit detail. Only shown while we're still
  // resolving — not when we've stopped to render the request-access
  // prompt or the removed/error states.
  if (isAuthed && !error && !info?.removed && !resolved && loading) {
    return (
      <Page>
        <Card>
          <Logo src={BpLogo} alt="Building Passport" />
          <Centered><CircularProgress size={26} /></Centered>
          <Subtitle>Opening this unit…</Subtitle>
        </Card>
      </Page>
    )
  }

  return (
    <Page>
      <Card>
        <Logo src={BpLogo} alt="Building Passport" />

        {loading && (
          <Centered><CircularProgress size={26} /></Centered>
        )}

        {!loading && error && (
          <>
            <ErrorState>{error}</ErrorState>
            <SecondaryButton onClick={() => setLoadAttempt(n => n + 1)}>
              Try again
            </SecondaryButton>
          </>
        )}

        {!loading && !error && info?.removed && (
          <>
            <HeroIcon><ApartmentOutlinedIcon style={{ fontSize: 32 }} /></HeroIcon>
            <Title>This unit is no longer active</Title>
            <Subtitle>
              The unit linked to this QR sticker has been removed from its building. If you think this is a mistake, please contact the building manager.
            </Subtitle>
            {info.buildingName && (
              <InfoBlock>
                <InfoLabel>Building</InfoLabel>
                <InfoValue>{info.buildingName}</InfoValue>
                {info.buildingAddress && <InfoSub>{info.buildingAddress}</InfoSub>}
              </InfoBlock>
            )}
          </>
        )}

        {!loading && !error && isAuthed && resolved && (
          <>
            <HeroIcon><ApartmentOutlinedIcon style={{ fontSize: 32 }} /></HeroIcon>
            <Title>Request access to this unit</Title>
            <Subtitle>
              You don't have access to this unit yet. Tell us your relationship and the building manager will review your request.
            </Subtitle>

            <InfoBlock>
              <InfoLabel>Unit</InfoLabel>
              <InfoValue>{resolved.unitName || 'Unit'}</InfoValue>
            </InfoBlock>

            <InfoBlock>
              <InfoLabel>Building</InfoLabel>
              <InfoValue>{resolved.buildingName || '—'}</InfoValue>
              {resolved.buildingAddress && <InfoSub>{resolved.buildingAddress}</InfoSub>}
            </InfoBlock>

            <Field>
              <FieldLabel>Your relationship to this unit</FieldLabel>
              <SearchableSelect
                value={relationType}
                onChange={v => setRelationType(v)}
                options={RELATION_OPTIONS}
                ariaLabel='Your relationship to this unit'
              />
            </Field>

            <PrimaryButton onClick={handleRequestAccess} disabled={submitting}>
              {submitting ? 'Sending…' : 'Request access'}
            </PrimaryButton>
            <SecondaryButton onClick={() => navigate('/bp-list')}>
              Not now
            </SecondaryButton>
          </>
        )}

        {!loading && !error && info && !info.removed && !isAuthed && (
          <>
            <HeroIcon><ApartmentOutlinedIcon style={{ fontSize: 32 }} /></HeroIcon>
            <Title>You're accessing a Unit Passport</Title>
            <Subtitle>
              Create a free account and we'll request access to this unit for you, or sign in if you've been here before — we'll ask about your relationship to the unit before sending the request.
            </Subtitle>

            <InfoBlock>
              <InfoLabel>Unit</InfoLabel>
              <InfoValue>{info.unitName || 'Unit'}</InfoValue>
            </InfoBlock>

            <InfoBlock>
              <InfoLabel>Building</InfoLabel>
              <InfoValue>{info.buildingName || '—'}</InfoValue>
              {info.buildingAddress && <InfoSub>{info.buildingAddress}</InfoSub>}
            </InfoBlock>

            <PrimaryButton onClick={handleSignupWithIntent}>
              Create an account &amp; request access
            </PrimaryButton>
            <SecondaryButton onClick={handleLogin}>
              I already have an account
            </SecondaryButton>
          </>
        )}
      </Card>
    </Page>
  )
}

export default UnitQrLanding
